import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {GraphicView} from '@app/modules/svg/element/complex/cartesian/GraphicView';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {Container} from '@app/modules/svg/Container';
import {Point} from '@app/modules/svg/model/Point';
import {NodeEditable} from '@app/modules/svg/element/shape/pointed/NodeEditable';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {Rect} from '@app/modules/svg/model/Rect';

export class SquareRootCursor extends ElementCursor {}

export interface SquareRootViewProperties extends ElementProperties {}

export class SquareRootView extends GraphicView implements NodeEditable, MoveDrawable {
  protected static readonly BASE_POINT_RADIUS: number = 7;

  protected override svgElement: SVGGElement = document.createElementNS(ElementView.svgURI, 'g');
  private svgParabolaElement: SVGPathElement = document.createElementNS(ElementView.svgURI, 'path');
  protected svgBasePointElement: SVGCircleElement = document.createElementNS(ElementView.svgURI, 'circle');

  protected override _type: ElementType = ElementType.SQUARE_ROOT;

  private _lastBasePoint: Point;
  private _lastBranchPoint: Point;

  private basePoint: Point;
  private branchPoint: Point;

  constructor(container: Container,
              properties: SquareRootViewProperties = {},
              basePoint: Point,
              branchPoint: Point,
              ownerId?: string,
              index?: number
  ) {
    super(
      container,
      properties,
      ElementView.toRect(basePoint, branchPoint),
      {x: 0, y: 0},
      (x) => x * x,
      undefined,
      ownerId,
      index
    );

    this.basePoint = basePoint;
    this.branchPoint = branchPoint;

    this.svgElement.appendChild(this.svgParabolaElement);
    this.svgElement.appendChild(this.svgBasePointElement);

    this.setAttr({
      'stroke-width': 0,
      fill: '#000',
      r: SquareRootView.BASE_POINT_RADIUS
    }, this.svgBasePointElement);

    this.setProperties(properties);

    this.__updateView__();
  }

  public updatePoints(basePoint: Point, branchPoint: Point): void {
    this.basePoint = basePoint;
    this.branchPoint = branchPoint;

    this._rect = ElementView.toRect(basePoint, branchPoint);

    let origin: Point;
    if (branchPoint.y < basePoint.y) {
      origin = {
        x: basePoint.x - this._rect.x,
        y: this._rect.height
      };
    } else {
      origin = {
        x: basePoint.x - this._rect.x,
        y: 0
      };
    }

    this.setOrigin(origin);
    this.f = this.parabolaFrom2Points(basePoint, branchPoint);
  }

  public override __updateView__(): void {
    if (!this.basePoint) {
      return;
    }

    this.setAttr({
      d: this._path.toString()
    }, this.svgParabolaElement);

    this.setAttr({
      cx: this.basePoint.x,
      cy: this.basePoint.y,
    }, this.svgBasePointElement);
  }

  public override __drawSize__(rect: Rect) {
    const basePoint: Point = {
      x: rect.x,
      y: rect.y,
    };

    const branchPoint: Point = {
      x: rect.x + rect.width,
      y: rect.y + rect.height,
    };

    super.__drawSize__(rect);

    this.updatePoints(basePoint, branchPoint);
  }

  public override __setRect__(rect: Rect) {
    let dw: number = 1;
    let dh: number = 1;

    if (this._lastRect.width !== 0) {
      dw = rect.width / (this._lastRect.width);
    }
    if (this._lastRect.height !== 0) {
      dh = rect.height / (this._lastRect.height);
    }

    this.updatePoints({
      x: rect.x + Math.abs(this._lastBasePoint.x - rect.x) * dw,
      y: rect.y + Math.abs(this._lastBasePoint.y - rect.y) * dh
    }, {
      x: rect.x + Math.abs(this._lastBranchPoint.x - rect.x) * dw,
      y: rect.y + Math.abs(this._lastBranchPoint.y - rect.y) * dh
    });
  }

  public override __correct__(refPoint: Point, lastRefPoint: Point): void {
    const delta: Point = this.__getCorrectionDelta__(refPoint, lastRefPoint);
    this.basePoint.x += delta.x;
    this.basePoint.y += delta.y;

    this.branchPoint.x += delta.x;
    this.branchPoint.y += delta.y;

    super.__correct__(refPoint, lastRefPoint);

    this.updatePoints(this.basePoint, this.branchPoint);
  }

  public override __drag__(delta: Point): void {
    super.__drag__(delta);

    this._rect.x += delta.x;
    this._rect.y += delta.y;

    const basePoint = {
      x: this.basePoint.x + delta.x,
      y: this.basePoint.y + delta.y,
    };
    const branchPoint = {
      x: this.branchPoint.x + delta.x,
      y: this.branchPoint.y + delta.y,
    };

    this.updatePoints(basePoint, branchPoint);
  }

  public override __fixRect__(): void {
    super.__fixRect__();
    this._lastBasePoint = Object.assign({}, this.basePoint);
    this._lastBranchPoint = Object.assign({}, this.branchPoint);
  }

  public isComplete(): boolean {
    return SquareRootView.pointsDistance(this.basePoint, this.branchPoint) > 10;
  }

  public get editablePoints(): Point[] {
    return [this.basePoint, this.branchPoint];
  }
  public get editablePointsCount(): number {
    return 2;
  }
  public replacePoint(index: number, point: Point): void {
    if (index === 0) {
      this.basePoint = point;
    } else if (index === 1) {
      this.branchPoint = point;
    }

    this.updatePoints(this.basePoint, this.branchPoint);
  }

  private resetOrigin(): void {
    const origin: Point = {
      x: 0,
      y: 0
    };

    if (this.branchPoint.y < this.basePoint.y) {
      origin.y = this._rect.height;
    } else {
      origin.y = 0;
    }

    if (this.branchPoint.x < this.basePoint.x) {
      origin.x = this._rect.width;
    } else {
      origin.x = 0;
    }

    const delta: Point = ElementView.delta(origin, this._origin);

    this.basePoint.x = this._lastBasePoint.x + delta.x;
    this.basePoint.y = this._lastBasePoint.y + delta.y;

    this.branchPoint.x = this._lastBranchPoint.x + delta.x;
    this.branchPoint.y = this._lastBranchPoint.y + delta.y;

    this.setOrigin(origin);
  }

  /**
   * y = a * sqrt(b * (x - h)) + k
   * h => basePoint.x
   * k => basePoint.y
   *
   * x => branchPoint.x
   * y => branchPoint.y
   * a =    (y - k)
   *      ------------
   *      sqrt(b * (x - h))
   * */
  private parabolaFrom2Points(basePoint: Point, branchPoint: Point): (x: number) => number {
    const h: number = basePoint.x / this._mainStepPhysicalUnit;
    const k: number = basePoint.y / this._mainStepPhysicalUnit;

    const x: number = branchPoint.x / this._mainStepPhysicalUnit;
    const y: number = branchPoint.y / this._mainStepPhysicalUnit;

    const b: number = x < h ? -1 : 1;

    let a: number = Math.abs((y - k) / Math.sqrt(b * (x - h)));

    if (branchPoint.y > basePoint.y) {
      a = -a;
    }

    return (inputX: number): number => a * Math.sqrt(b * inputX);
  }

  public override toJSON(): any {
    const json: any = super.toJSON();
    json.basePoint = this.basePoint;
    json.branchPoint = this.branchPoint;
    return json;
  }

  public override fromJSON(json: any): void {
    this.basePoint = json.basePoint;
    this.branchPoint = json.branchPoint;
    this.f = this.parabolaFrom2Points(json.basePoint, json.branchPoint);
    super.fromJSON(json);
  };
}
